import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";

import { CihClosingCta } from "~/components/cih/closing-cta";
import {
  CihCoachCellGrid,
  type CihCoachCellData,
} from "~/components/cih/coach-cell";
import { CihPagination } from "~/components/cih/pagination";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { getCoachesByCtpSlug, getCtpBySlug } from "~/lib/functions/ctps";
import { createBrandMeta } from "~/lib/shared/meta";

const PAGE_SIZE = 24;

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
});

type CtpData = NonNullable<Awaited<ReturnType<typeof getCtpBySlug>>["ctp"]>;

type LoaderData = {
  title: string;
  ctp: CtpData;
  coaches: CihCoachCellData[];
  total: number;
  page: number;
};

export const Route = createFileRoute(
  "/_public/training-programs/$slug/coaches",
)({
  staticData: { title: "Coaches" },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ params, deps }): Promise<LoaderData> => {
    const [{ ctp }, result] = await Promise.all([
      getCtpBySlug({ data: { slug: params.slug } }),
      getCoachesByCtpSlug({
        data: { slug: params.slug, page: deps.page, limit: PAGE_SIZE },
      }),
    ]);
    if (!ctp) throw notFound();

    const cells: CihCoachCellData[] = result.coaches.map(
      ({ coach, profile }) => ({
        id: coach.id,
        username: coach.username,
        fullName: coach.fullName,
        profilePictureUrl: coach.profilePictureUrl,
        credential: profile?.coachingTitle ?? profile?.title ?? null,
        description: null,
      }),
    );

    return {
      title: `${ctp.name} coaches`,
      ctp,
      coaches: cells,
      total: result.total,
      page: result.page,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.ctp.name ?? params.slug;
    const page = loaderData?.page ?? 1;
    const pageSuffix = page > 1 ? ` — Page ${page}` : "";
    return createBrandMeta({
      title: `${name} coaches${pageSuffix} | Dream Coach Match`,
      description: `All coaches trained at ${name}.`,
      ogUrl: `https://dreamcoachmatch.com/training-programs/${params.slug}/coaches`,
      canonical: `https://dreamcoachmatch.com/training-programs/${params.slug}/coaches`,
    });
  },
  component: CtpCoachesPage,
});

function CtpCoachesPage() {
  const { ctp, coaches, total, page } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const buildHref = (p: number) =>
    p === 1
      ? `/training-programs/${slug}/coaches`
      : `/training-programs/${slug}/coaches?page=${p}`;

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Training programs", href: "/training-programs" },
              { label: ctp.name, href: `/training-programs/${slug}` },
              { label: "Coaches" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-orange-700 uppercase">
            ● {ctp.name}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            {ctp.name} coaches
          </h1>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
        <CihCoachCellGrid coaches={coaches} />
      </div>

      <CihPagination
        page={page}
        totalPages={totalPages}
        buildHref={buildHref}
      />

      <CihClosingCta
        headline={`Find a coach trained at ${ctp.name}.`}
        cta={
          <Link to="/discovery" search={{ source: `ctp:${slug}:coaches` }}>
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
